import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment-timezone';
import { ArrowLeftIcon, PaperAirplaneIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

export default function NovaCampanha() {
    const navigate = useNavigate();
    const [assistentes, setAssistentes] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [previewNumbers, setPreviewNumbers] = useState([]);
    const [planilhaData, setPlanilhaData] = useState([]);
    const [horaAtual, setHoraAtual] = useState(moment().tz('America/Sao_Paulo'));
    const [minDateTime, setMinDateTime] = useState(() => moment().tz('America/Sao_Paulo').startOf('minute').format('YYYY-MM-DDTHH:mm'));
    const [isAgendamentoFocused, setIsAgendamentoFocused] = useState(false);
    const [assistenteFiltroNome, setAssistenteFiltroNome] = useState('');
    const [binaFiltroNome, setBinaFiltroNome] = useState('');
    const [isAssistenteDropdownOpen, setIsAssistenteDropdownOpen] = useState(false);
    const [isBinaDropdownOpen, setIsBinaDropdownOpen] = useState(false);
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const clienteId = userData.clienteId || userData._id || userData.id;
    const isAdmin = userData.role === 'admin' || userData.role === 'super_admin';
    const isTenantAdmin = userData.role === 'admin';

    const [formData, setFormData] = useState({
        nome: '',
        objetivo: '',
        contatos: '',
        agendamento: moment().tz('America/Sao_Paulo').format('YYYY-MM-DDTHH:mm'),
        assistenteSelecionado: '',
        phoneNumberSelecionado: '',
        modo: 'ativa',
        status: 'pendente',
        qtdTentativasPorNumero: 3, // Valor padrão de 3 tentativas
        followUpConfig: {
            habilitado: false,
            maxTentativasTotal: 4,
            maxTentativasPorDia: 1,
            intervaloMinutos: 1440,
            horaInicioDisparos: '08:00',
            horaFimDisparos: '18:00',
            reativarCicloAposLimiteAtivo: false,
            reativarCicloQuantidade: 0,
            reativarCicloUnidade: 'semanas',
            numerosExcluidos: '',
            assistenteFollowUpId: '',
            phoneNumberFollowUpId: '',
            uazapiToken: '',
            uazapiMensagemLimite: '',
            uazapiMensagensNaoAtendeu: ['']
        },
        // Horário de funcionamento (obrigatório)
        usarFuncionamento: true,
        funcionamento: {
            dataInicio: moment().tz('America/Sao_Paulo').format('YYYY-MM-DD'),
            dataFim: moment().tz('America/Sao_Paulo').add(1, 'month').format('YYYY-MM-DD'),
            diasSemana: [1, 2, 3, 4, 5], // Seg a Sex
            horaInicio: '08:00',
            horaFim: '18:00'
        }
    });
    const [showAllNaoAtendeuMessages, setShowAllNaoAtendeuMessages] = useState(false);

    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
    // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';
    const tokenClient = localStorage.getItem('tokenClient');

    const getAuthConfig = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Sessão expirada. Faça login novamente.');
            return null;
        }
        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    };

    const formatPhoneNumber = (number) => {
        if (!number) return null;
        // Remove qualquer caractere não numérico
        let cleaned = ('' + number).replace(/\D/g, '');
        // Verifica se já começa com +55 ou 55
        if (cleaned.startsWith('55')) {
            if (!cleaned.startsWith('+55')) {
                cleaned = `+${cleaned}`;
            }
        } else {
            cleaned = `+55${cleaned}`;
        }
        // Verifica se o número tem 13 dígitos (incluindo +55)
        if (cleaned.length !== 13) {
            // Se não tiver, adiciona zeros à esquerda até completar 13 dígitos
            while (cleaned.length < 13) {
                cleaned = cleaned.substring(0, 3) + '0' + cleaned.substring(3);
            }
        }
        return cleaned;
    };

    const fetchInitialData = useCallback(async () => {
        const toastId = toast.loading('Carregando dados necessários...');
        try {
            const clienteIdFromStorage = clienteId;
            const role = userData.role; // Obtendo o role do usuário
            // Se não for admin e não tiver clienteId, redirecionar
            if (!clienteIdFromStorage && !isAdmin) {
                toast.error('Cliente não identificado. Faça login novamente.', { id: toastId });
                navigate('/');
                return;
            }

            const authConfig = getAuthConfig();
            if (!authConfig) {
                toast.error('Sessão expirada. Faça login novamente.', { id: toastId });
                navigate('/');
                return;
            }

            // Apenas passa clienteId como parâmetro se não for admin/super_admin
            const params = !isAdmin && clienteId ? { clienteId } : {};

            if (tokenClient) {
                params.tokenClient = tokenClient;
            }

            const config = {
                ...authConfig,
                params,
            };
            const [assistentesRes, phoneNumbersRes, sipsRes] = await Promise.all([
                axios.get(`${baseUrl}/api/assistentes/master`, config),
                axios.get(`${baseUrl}/api/phoneNumbers`, config),
                axios.get(`${baseUrl}/api/sips`, config),
            ]);
            console.log("sipsRes", sipsRes.data);
            const phoneNumbersComSips = phoneNumbersRes.data.map(pn => ({
                ...pn,
                sipAssociado: sipsRes.data.find(sip => sip._id === (pn.credentialId?._id || pn.credentialId)) || null,
            }));
            console.log("phoneNumbersComSips", phoneNumbersComSips);
            setAssistentes(assistentesRes.data);
            setPhoneNumbers(phoneNumbersComSips);
            toast.success('Dados carregados!', { id: toastId });
        } catch (err) {
            toast.error('Não foi possível carregar os dados.', { id: toastId });
            console.error(err);
        } finally {
            setLoadingInitialData(false);
        }
    }, [navigate, clienteId, isAdmin, baseUrl, tokenClient]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Filtrar assistentes
    const assistentesFiltrados = useMemo(() => {
        let lista = assistentes;

        // Admin (não super) não deve ver assistentes sem cliente associado (N/A)
        if (isTenantAdmin) {
            lista = lista.filter(a => !!(a.clienteId && (a.clienteId._id || a.clienteId.id || a.clienteId)));
        }

        if (!assistenteFiltroNome) return lista;
        
        const nomeLower = assistenteFiltroNome.toLowerCase();
        return lista.filter(a => {
            const nome = (a.nome || a.name || '').toLowerCase();
            return nome.includes(nomeLower);
        });
    }, [assistentes, assistenteFiltroNome, isTenantAdmin]);

    // Filtrar números/Bina
    const binasFiltradas = useMemo(() => {
        if (!binaFiltroNome) return phoneNumbers;
        
        const nomeLower = binaFiltroNome.toLowerCase();
        return phoneNumbers.filter(n => {
            const nome = (n.name || '').toLowerCase();
            const numero = (n.number || '').toLowerCase();
            return nome.includes(nomeLower) || numero.includes(nomeLower);
        });
    }, [phoneNumbers, binaFiltroNome]);

    // Refs para fechar dropdowns ao clicar fora
    const assistenteDropdownRef = useRef(null);
    const binaDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (assistenteDropdownRef.current && !assistenteDropdownRef.current.contains(event.target)) {
                setIsAssistenteDropdownOpen(false);
            }
            if (binaDropdownRef.current && !binaDropdownRef.current.contains(event.target)) {
                setIsBinaDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Busca a hora atual do servidor a cada segundo
    useEffect(() => {
        const buscarHoraServidor = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/server-time`);
                if (response.data && response.data.hora) {
                    const horaServidor = moment(response.data.hora, 'YYYY-MM-DD HH:mm:ss').tz('America/Sao_Paulo');
                    setHoraAtual(horaServidor);
                    
                    // Atualizar minDateTime apenas quando o minuto mudar (não a cada segundo)
                    // E não atualizar se o campo estiver em foco (usuário está interagindo)
                    if (!isAgendamentoFocused) {
                        const novoMinuto = horaServidor.clone().startOf('minute').format('YYYY-MM-DDTHH:mm');
                        setMinDateTime(prevMin => {
                            // Só atualiza se o minuto realmente mudou
                            if (prevMin !== novoMinuto) {
                                return novoMinuto;
                            }
                            return prevMin;
                        });
                    }
                    
                    // Atualizar o campo de agendamento se ainda não foi definido ou se estiver no passado
                    // Comparar apenas até o minuto (ignorar segundos) para permitir o mesmo minuto
                    setFormData(prev => {
                        if (!prev.agendamento) {
                            return {
                                ...prev,
                                agendamento: horaServidor.clone().startOf('minute').format('YYYY-MM-DDTHH:mm')
                            };
                        } else {
                            const prevMinuto = moment(prev.agendamento).tz('America/Sao_Paulo').startOf('minute');
                            const servidorMinuto = horaServidor.clone().startOf('minute');
                            if (prevMinuto.isBefore(servidorMinuto)) {
                                return {
                                    ...prev,
                                    agendamento: servidorMinuto.format('YYYY-MM-DDTHH:mm')
                                };
                            }
                        }
                        return prev;
                    });
                }
            } catch (err) {
                console.error('Erro ao buscar hora do servidor, usando hora local:', err);
                // Fallback para hora local se o servidor não responder
                const horaLocal = moment().tz('America/Sao_Paulo');
                setHoraAtual(horaLocal);
                const novoMinuto = horaLocal.clone().startOf('minute').format('YYYY-MM-DDTHH:mm');
                setMinDateTime(prevMin => prevMin !== novoMinuto ? novoMinuto : prevMin);
            }
        };

        // Buscar imediatamente
        buscarHoraServidor();

        // Atualizar a cada segundo
        const interval = setInterval(buscarHoraServidor, 1000);
        return () => clearInterval(interval);
    }, [baseUrl]);

    const toggleDiaSemana = (dia) => {
        setFormData(prev => {
            const dias = prev.funcionamento.diasSemana || [];
            const novaLista = dias.includes(dia)
                ? dias.filter(d => d !== dia)
                : [...dias, dia].sort((a, b) => a - b);
            return {
                ...prev,
                funcionamento: { ...prev.funcionamento, diasSemana: novaLista }
            };
        });
    };

    const handleFuncionamentoChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            funcionamento: { ...prev.funcionamento, [field]: value }
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'usarFuncionamento') {
            setFormData(prev => ({ ...prev, usarFuncionamento: e.target.checked }));
            return;
        }
        if (name.startsWith('funcionamento.')) {
            const field = name.split('.')[1];
            handleFuncionamentoChange(field, value);
            return;
        }
        if (name.startsWith('followUpConfig.')) {
            const field = name.split('.')[1];
            const numericFields = new Set(['maxTentativasTotal', 'maxTentativasPorDia', 'intervaloMinutos', 'reativarCicloQuantidade']);
            const fieldValue =
                field === 'habilitado'
                    ? e.target.checked
                    : numericFields.has(field)
                        ? Number(value)
                        : value;
            setFormData(prev => ({
                ...prev,
                followUpConfig: {
                    ...prev.followUpConfig,
                    [field]: field === 'habilitado' ? fieldValue : numericFields.has(field) ? fieldValue : value
                }
            }));
            return;
        }
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // Se o campo de agendamento foi alterado, validar se não está no passado
            if (name === 'agendamento') {
                const dataAgendamento = moment(value).tz('America/Sao_Paulo');
                // Usar a hora do servidor (horaAtual) ao invés da hora local
                // Comparar apenas até o minuto (ignorar segundos) para permitir o mesmo minuto
                const horaAtualMinuto = horaAtual.clone().startOf('minute');
                const dataAgendamentoMinuto = dataAgendamento.clone().startOf('minute');
                
                if (dataAgendamentoMinuto.isBefore(horaAtualMinuto)) {
                    // Se a data selecionada estiver no passado, atualizar para a hora atual do servidor
                    toast.error('Não é possível agendar no passado. Ajustando para a hora atual do servidor.', {
                        duration: 3000,
                        icon: '⚠️'
                    });
                    newData.agendamento = horaAtualMinuto.format('YYYY-MM-DDTHH:mm');
                }
            }
            return newData;
        });
    };

    const handleNaoAtendeuMessageChange = (index, value) => {
        setFormData(prev => {
            const list = Array.isArray(prev.followUpConfig?.uazapiMensagensNaoAtendeu)
                ? [...prev.followUpConfig.uazapiMensagensNaoAtendeu]
                : [''];
            list[index] = value;
            return {
                ...prev,
                followUpConfig: {
                    ...prev.followUpConfig,
                    uazapiMensagensNaoAtendeu: list
                }
            };
        });
    };

    const addNaoAtendeuMessage = () => {
        setFormData(prev => {
            const list = Array.isArray(prev.followUpConfig?.uazapiMensagensNaoAtendeu)
                ? [...prev.followUpConfig.uazapiMensagensNaoAtendeu]
                : [];
            list.push('');
            return {
                ...prev,
                followUpConfig: {
                    ...prev.followUpConfig,
                    uazapiMensagensNaoAtendeu: list
                }
            };
        });
        setShowAllNaoAtendeuMessages(true);
    };

    const removeNaoAtendeuMessage = (index) => {
        setFormData(prev => {
            const list = Array.isArray(prev.followUpConfig?.uazapiMensagensNaoAtendeu)
                ? [...prev.followUpConfig.uazapiMensagensNaoAtendeu]
                : [''];
            if (list.length <= 1) {
                return {
                    ...prev,
                    followUpConfig: {
                        ...prev.followUpConfig,
                        uazapiMensagensNaoAtendeu: ['']
                    }
                };
            }
            list.splice(index, 1);
            const nextList = list.length ? list : [''];
            if (nextList.length <= 2) {
                setShowAllNaoAtendeuMessages(false);
            }
            return {
                ...prev,
                followUpConfig: {
                    ...prev.followUpConfig,
                    uazapiMensagensNaoAtendeu: nextList
                }
            };
        });
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        const fileExtension = file.name.split('.').pop().toLowerCase();
        reader.onload = (event) => {
            const content = event.target.result;
            if (fileExtension === 'csv') {
                // Process CSV file com encoding UTF-8 para suportar acentos
                Papa.parse(content, {
                    header: true, // Assume a primeira linha como cabeçalho
                    encoding: 'UTF-8', // Especifica encoding UTF-8
                    skipEmptyLines: true, // Pula linhas vazias
                    transformHeader: (header) => {
                        // Preserva acentos nos cabeçalhos
                        return header ? header.trim() : '';
                    },
                    transform: (value) => {
                        // Preserva acentos nos valores
                        return value ? String(value).trim() : '';
                    },
                    complete: (result) => {
                        const allData = result.data;
                        // Processa os dados para separar colunas com ponto e vírgula
                        const processedData = allData
                            .filter(row => row && Object.keys(row).length > 0) // Remove linhas vazias
                            .map(row => {
                                const newRow = {};
                                Object.keys(row).forEach(key => {
                                    if (!key) return; // Pula chaves vazias
                                    
                                    const value = row[key];
                                    const valueStr = value !== undefined && value !== null 
                                        ? String(value).trim() 
                                        : '';
                                    
                                    // Verifica se o cabeçalho ou valor contém ponto e vírgula
                                    const keyParts = String(key).split(';');
                                    const valueParts = valueStr.split(';');
                                    
                                    if (keyParts.length > 1 && valueParts.length === keyParts.length) {
                                        keyParts.forEach((part, index) => {
                                            newRow[part.trim()] = valueParts[index] ? valueParts[index].trim() : '';
                                        });
                                    } else {
                                        newRow[key.trim()] = valueStr;
                                    }
                                });
                                return newRow;
                            });
                        setPlanilhaData(processedData);
                        const headers = Object.keys(processedData[0] || {});
                        const phoneColumns = headers.filter(header =>
                            header.toLowerCase().includes('telefones')
                        );
                        if (phoneColumns.length === 0) {
                            toast.error('Nenhuma coluna de telefones encontrada.');
                            return;
                        }
                        const phoneNumbers = [];
                        processedData.forEach(row => {
                            const rowNumbers = new Set();
                            phoneColumns.forEach(column => {
                                const cellValue = row[column];
                                if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
                                    const valueStr = String(cellValue).trim();
                                    if (valueStr) {
                                        const formattedNumber = formatPhoneNumber(valueStr);
                                        if (formattedNumber) {
                                            rowNumbers.add(formattedNumber);
                                        }
                                    }
                                }
                            });
                            rowNumbers.forEach(number => phoneNumbers.push(number));
                        });
                        if (phoneNumbers.length === 0) {
                            toast.error('Nenhum número de telefones válido encontrado.');
                            return;
                        }
                        setPreviewNumbers(phoneNumbers);
                        setFormData(prev => ({ ...prev, contatos: phoneNumbers.join('\n') }));
                        toast.success(`Números carregados com sucesso! ${phoneNumbers.length} número(s) encontrado(s).`);
                    },
                    error: (error) => {
                        toast.error('Erro ao processar o arquivo CSV.');
                        console.error(error);
                    }
                });
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                try {
                    // Process Excel file com suporte a UTF-8 e acentos
                    const workbook = XLSX.read(content, { 
                        type: 'binary',
                        cellDates: true,
                        cellNF: false,
                        cellText: false
                    });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    
                    // Converter para JSON preservando acentos e caracteres especiais
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,
                        defval: '', // Valor padrão para células vazias
                        raw: false // Processa valores como strings para preservar formatação
                    });
                    
                    // Pega o cabeçalho (primeira linha)
                    const headers = (jsonData[0] || []).map(h => h ? String(h).trim() : '');
                    // Pega os dados (a partir da segunda linha)
                    const rows = jsonData.slice(1);
                    
                    // Armazena todos os dados em um array de objetos
                    const allData = rows.map(row => {
                        const rowObject = {};
                        headers.forEach((header, index) => {
                            if (!header) return; // Pula cabeçalhos vazios
                            
                            const value = row[index] !== undefined && row[index] !== null 
                                ? String(row[index]).trim() 
                                : '';
                            
                            // Processa o cabeçalho e valor para separar colunas com ponto e vírgula
                            const headerParts = String(header).split(';');
                            const valueParts = value.split(';');
                            
                            if (headerParts.length > 1 && valueParts.length === headerParts.length) {
                                headerParts.forEach((part, i) => {
                                    rowObject[part.trim()] = valueParts[i] ? valueParts[i].trim() : '';
                                });
                            } else {
                                rowObject[header] = value;
                            }
                        });
                        return rowObject;
                    }).filter(row => Object.keys(row).length > 0); // Remove linhas completamente vazias
                    setPlanilhaData(allData);
                    
                    // Encontre os índices de todas as colunas de telefones
                    const phoneIndices = [];
                    headers.forEach((header, index) => {
                        if (header && String(header).toLowerCase().includes('telefones')) {
                            phoneIndices.push(index);
                        }
                    });
                    
                    if (phoneIndices.length === 0) {
                        toast.error('Nenhuma coluna de telefones encontrada.');
                        return;
                    }
                    
                    const phoneNumbers = [];
                    rows.forEach(row => { // Ignora o cabeçalho
                        if (!row || !Array.isArray(row)) return;
                        
                        const rowNumbers = new Set();
                        phoneIndices.forEach(index => {
                            const cellValue = row[index];
                            if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
                                const valueStr = String(cellValue).trim();
                                if (valueStr) {
                                    const formattedNumber = formatPhoneNumber(valueStr);
                                    if (formattedNumber) {
                                        rowNumbers.add(formattedNumber);
                                    }
                                }
                            }
                        });
                        rowNumbers.forEach(number => phoneNumbers.push(number));
                    });
                    
                    if (phoneNumbers.length === 0) {
                        toast.error('Nenhum número de telefones válido encontrado.');
                        return;
                    }
                    
                    setPreviewNumbers(phoneNumbers);
                    setFormData(prev => ({ ...prev, contatos: phoneNumbers.join('\n') }));
                    toast.success(`Números carregados com sucesso! ${phoneNumbers.length} número(s) encontrado(s).`);
                } catch (error) {
                    console.error('Erro ao processar arquivo Excel:', error);
                    toast.error('Erro ao processar o arquivo Excel. Verifique se o arquivo está no formato correto.');
                }
            } else {
                toast.error('Formato de arquivo não suportado. Use CSV ou XLSX.');
            }
        };
        
        // Configurar o encoding correto para cada tipo de arquivo
        if (fileExtension === 'csv') {
            // Usa UTF-8 para CSV para preservar acentos
            reader.readAsText(file, 'UTF-8');
        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            // Usa binary string para Excel (XLSX já suporta UTF-8 internamente)
            reader.readAsBinaryString(file);
        } else {
            toast.error('Formato de arquivo não suportado. Use CSV ou XLSX.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { nome, objetivo, contatos, agendamento, assistenteSelecionado, phoneNumberSelecionado, modo, status, qtdTentativasPorNumero, followUpConfig, usarFuncionamento, funcionamento } = formData;
        
        // Validação detalhada
        if (!nome || !nome.trim()) {
            return toast.error('O nome da campanha é obrigatório.');
        }
        if (!objetivo || !objetivo.trim()) {
            return toast.error('O objetivo da campanha é obrigatório.');
        }
        if (!contatos || !contatos.trim()) {
            return toast.error('A lista de contatos é obrigatória.');
        }
        if (!agendamento) {
            return toast.error('A data de agendamento é obrigatória.');
        }
        if (!assistenteSelecionado) {
            return toast.error('Selecione um assistente.');
        }
        if (!phoneNumberSelecionado) {
            return toast.error('Selecione um número de origem (Bina).');
        }
        if (!formData.funcionamento?.diasSemana || formData.funcionamento.diasSemana.length === 0) {
            return toast.error('Selecione pelo menos um dia da semana para o horário de funcionamento.');
        }
        if (!formData.funcionamento?.dataInicio) {
            return toast.error('Informe a data de início do período de funcionamento.');
        }
        if (!formData.funcionamento?.dataFim) {
            return toast.error('Informe a data de fim do período de funcionamento.');
        }
        
        // Validar se a data de agendamento não está no passado (usando hora do servidor)
        // Comparar apenas até o minuto (ignorar segundos) para permitir o mesmo minuto
        const dataAgendamento = moment(agendamento).tz('America/Sao_Paulo').startOf('minute');
        const horaAtualMinuto = horaAtual.clone().startOf('minute');
        if (dataAgendamento.isBefore(horaAtualMinuto)) {
            return toast.error(`A data de agendamento não pode ser no passado. Hora atual do servidor: ${horaAtual.format('DD/MM/YYYY HH:mm:ss')}`);
        }
        
        const contatosArray = contatos.split('\n').map(c => c.trim()).filter(c => c);
        if (contatosArray.length === 0) {
            return toast.error('Forneça ao menos um contato válido.');
        }
        if (followUpConfig?.habilitado) {
            if ((Number(followUpConfig.maxTentativasTotal) || 0) < 1) return toast.error('Follow-up: máximo total deve ser ao menos 1.');
            if ((Number(followUpConfig.maxTentativasPorDia) || 0) < 1) return toast.error('Follow-up: máximo por dia deve ser ao menos 1.');
            if ((Number(followUpConfig.intervaloMinutos) || 0) < 1) return toast.error('Follow-up: intervalo mínimo é 1 minuto.');
            if (!/^\d{2}:\d{2}$/.test(String(followUpConfig.horaInicioDisparos || ''))) return toast.error('Follow-up: horário de início inválido.');
            if (!/^\d{2}:\d{2}$/.test(String(followUpConfig.horaFimDisparos || ''))) return toast.error('Follow-up: horário de fim inválido.');
            if (followUpConfig.reativarCicloAposLimiteAtivo && (Number(followUpConfig.reativarCicloQuantidade) || 0) < 1) {
                return toast.error('Follow-up: para reativar ciclo, informe uma quantidade maior que zero.');
            }
        }

        const authConfig = getAuthConfig();
        if (!authConfig) {
            return;
        }

        setIsSubmitting(true);
        const toastId = toast.loading('Criando campanha...');
        try {
            const assistenteObj = assistentes.find(a => a.vapiId === assistenteSelecionado);
            const phoneNumberObj = phoneNumbers.find(pn => pn._id === phoneNumberSelecionado);
            
            if (!assistenteObj) {
                throw new Error('Assistente selecionado não encontrado.');
            }
            if (!phoneNumberObj) {
                throw new Error('Número de telefone selecionado não encontrado.');
            }
            if (!phoneNumberObj.sipAssociado) {
                throw new Error('O número selecionado não possui um SIP válido associado.');
            }
            // Verificar se o phoneNumber tem vapiId, caso contrário usar _id
            const phoneNumberIdToUse = phoneNumberObj.vapiId || phoneNumberObj._id || phoneNumberObj.id;
            if (!phoneNumberIdToUse) {
                throw new Error('O número selecionado não possui um ID válido.');
            }
            
            // Verificar se o assistente tem vapiId
            if (!assistenteObj.vapiId) {
                throw new Error('O assistente selecionado não possui um ID de provedor válido.');
            }
            
            // Cria o payload básico
            const payload = {
                nome: nome.trim(),
                objetivo: objetivo.trim(),
                contatos: contatosArray,
                agendamento: moment(agendamento).tz('America/Sao_Paulo').format(),
                assistantId: assistenteObj.vapiId,
                phoneNumberId: phoneNumberIdToUse,
                modo: modo || 'ativa',
                status: status || 'pendente',
                sip: phoneNumberObj.sipAssociado,
                tokenClient: tokenClient || '', // Adiciona o tokenClient ao payload
                dadosPlanilha: planilhaData || [], // Adiciona todos os dados da planilha
                qtdTentativasPorNumero: qtdTentativasPorNumero || 3, // Adiciona o número de tentativas por número
                followUpConfig: {
                    habilitado: !!followUpConfig?.habilitado,
                    maxTentativasTotal: Number(followUpConfig?.maxTentativasTotal || 4),
                    maxTentativasPorDia: Number(followUpConfig?.maxTentativasPorDia || 1),
                    intervaloMinutos: Number(followUpConfig?.intervaloMinutos || 1440),
                    horaInicioDisparos: followUpConfig?.horaInicioDisparos || '08:00',
                    horaFimDisparos: followUpConfig?.horaFimDisparos || '18:00',
                    reativarCicloAposLimiteAtivo: followUpConfig?.reativarCicloAposLimiteAtivo === true,
                    reativarCicloQuantidade: Number(followUpConfig?.reativarCicloQuantidade || 0),
                    reativarCicloUnidade: followUpConfig?.reativarCicloUnidade || 'semanas',
                    numerosExcluidos: String(followUpConfig?.numerosExcluidos || '')
                        .split(/\r?\n/)
                        .map((x) => x.trim())
                        .filter(Boolean),
                    assistenteFollowUpId: followUpConfig?.assistenteFollowUpId?.trim() || null,
                    phoneNumberFollowUpId: followUpConfig?.phoneNumberFollowUpId?.trim() || null,
                    uazapiToken: followUpConfig?.uazapiToken?.trim() || null,
                    uazapiMensagemLimite: followUpConfig?.uazapiMensagemLimite?.trim() || null,
                    uazapiMensagensNaoAtendeu: (Array.isArray(followUpConfig?.uazapiMensagensNaoAtendeu)
                        ? followUpConfig.uazapiMensagensNaoAtendeu
                        : [])
                        .map((x) => x.trim())
                        .filter(Boolean)
                },
                funcionamento: {
                    dataInicio: funcionamento.dataInicio ? moment(funcionamento.dataInicio).tz('America/Sao_Paulo').startOf('day').format() : null,
                    dataFim: funcionamento.dataFim ? moment(funcionamento.dataFim).tz('America/Sao_Paulo').endOf('day').format() : null,
                    diasSemana: funcionamento.diasSemana || [],
                    horaInicio: funcionamento.horaInicio || '08:00',
                    horaFim: funcionamento.horaFim || '18:00'
                }
            };
            payload.clienteId = clienteId;
            
            console.log('Payload sendo enviado:', payload);
            await axios.post(`${baseUrl}/api/campanhas`, payload, authConfig);
            toast.success('Campanha criada e agendada com sucesso!', { id: toastId });
            navigate('/campanhas');
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erro ao criar a campanha.';
            toast.error(errorMessage, { id: toastId });
            console.error('Erro completo:', err);
            console.error('Response data:', err.response?.data);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderPreview = () => {
        if (previewNumbers.length === 0 && planilhaData.length === 0) return null;
        return (
            <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Pré-visualização dos Dados</h3>
                {planilhaData.length > 0 && (
                    <div className="overflow-x-auto mb-4">
                        <table className="min-w-full bg-gray-700 rounded-lg">
                            <thead>
                                <tr>
                                    {Object.keys(planilhaData[0] || {}).map(header => (
                                        <th key={header} className="p-2 border-b border-gray-600">{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {planilhaData.map((row, index) => (
                                    <tr key={index}>
                                        {Object.values(row).map((value, i) => (
                                            <td key={i} className="p-2 border-b border-gray-600">{value}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {previewNumbers.length > 0 && (
                    <div className="bg-gray-700 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold mb-2">Pré-visualização dos Números</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {previewNumbers.map((number) => (
                                <div key={number} className="bg-gray-800 p-2 rounded">
                                    {number}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const inputStyle = "w-full px-3 py-2 mt-1 text-gray-200 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-500";
    const labelStyle = "text-sm font-medium text-gray-300";

    if (loadingInitialData) {
        return <div className="p-8 text-white text-center">Preparando formulário...</div>;
    }

  return (
    <div className="space-y-6">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center mb-6">
                    <button onClick={() => navigate(-1)} className="p-2 mr-4 text-gray-400 hover:text-white rounded-full hover:bg-gray-700">
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <h1 className="text-3xl font-bold">Nova Campanha</h1>
                </div>
                <form onSubmit={handleSubmit} className="bg-gray-800 bg-opacity-60 p-8 rounded-xl shadow-lg space-y-6">
                    <div>
                        <label htmlFor="nome" className={labelStyle}>Nome da Campanha</label>
                        <input id="nome" name="nome" type="text" value={formData.nome} onChange={handleChange} className={inputStyle} required placeholder="Ex: Prospecção de Leads - Agosto" />
                    </div>
                    <div>
                        <label htmlFor="objetivo" className={labelStyle}>Objetivo da Campanha</label>
                        <input id="objetivo" name="objetivo" type="text" value={formData.objetivo} onChange={handleChange} className={inputStyle} required placeholder="Ex: qualificar lead, cobrança amigável" />
                    </div>
                    <div>
                        <label htmlFor="contatos" className={labelStyle}>Lista de Contatos (um por linha)</label>
                        <textarea id="contatos" name="contatos" value={formData.contatos} onChange={handleChange} className={`${inputStyle} h-40 font-mono`} placeholder="Cole seus contatos aqui. Ex: +5511999998888" required />
                    </div>
                    <div>
                        <label htmlFor="file-upload" className={labelStyle}>Ou faça upload de um arquivo CSV/XLSX</label>
                        <input id="file-upload" name="file-upload" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className={inputStyle} />
                        {renderPreview()}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Select de Assistente com Busca */}
                        <div className="relative" ref={assistenteDropdownRef}>
                            <label className={labelStyle}>Assistente de Voz</label>
                            <button
                                type="button"
                                onClick={() => setIsAssistenteDropdownOpen(!isAssistenteDropdownOpen)}
                                className={`${inputStyle} w-full text-left flex items-center justify-between`}
                            >
                                <span>
                                    {formData.assistenteSelecionado
                                        ? assistentes.find(a => a.vapiId === formData.assistenteSelecionado)?.nome || assistentes.find(a => a.vapiId === formData.assistenteSelecionado)?.name || 'Selecione um assistente'
                                        : 'Selecione um assistente'}
                                </span>
                                <ChevronDownIcon className={`h-5 w-5 transition-transform ${isAssistenteDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isAssistenteDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-96 overflow-hidden">
                                    <div className="p-2 border-b border-gray-600">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome..."
                                                value={assistenteFiltroNome}
                                                onChange={(e) => setAssistenteFiltroNome(e.target.value)}
                                                className="w-full pl-8 pr-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    <div className="overflow-y-auto max-h-64">
                                        {assistentesFiltrados.length === 0 ? (
                                            <div className="p-3 text-gray-400 text-sm text-center">Nenhum assistente encontrado</div>
                                        ) : (
                                            assistentesFiltrados.map(a => (
                                                <button
                                                    key={`assistente-${a.vapiId}`}
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, assistenteSelecionado: a.vapiId }));
                                                        setIsAssistenteDropdownOpen(false);
                                                        setAssistenteFiltroNome('');
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                                                        formData.assistenteSelecionado === a.vapiId ? 'bg-purple-600 text-white' : 'text-gray-200'
                                                    }`}
                                                >
                                                    {a.nome || a.name}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Select de Bina com Busca */}
                        <div className="relative" ref={binaDropdownRef}>
                            <label className={labelStyle}>Número de Origem (Bina)</label>
                            <button
                                type="button"
                                onClick={() => setIsBinaDropdownOpen(!isBinaDropdownOpen)}
                                className={`${inputStyle} w-full text-left flex items-center justify-between`}
                            >
                                <span>
                                    {formData.phoneNumberSelecionado
                                        ? (() => {
                                            const selected = phoneNumbers.find(n => n._id === formData.phoneNumberSelecionado);
                                            return selected ? `${selected.name} (${selected.number})` : 'Selecione um número';
                                        })()
                                        : 'Selecione um número'}
                                </span>
                                <ChevronDownIcon className={`h-5 w-5 transition-transform ${isBinaDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isBinaDropdownOpen && (
                                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-96 overflow-hidden">
                                    <div className="p-2 border-b border-gray-600">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome ou número..."
                                                value={binaFiltroNome}
                                                onChange={(e) => setBinaFiltroNome(e.target.value)}
                                                className="w-full pl-8 pr-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    <div className="overflow-y-auto max-h-64">
                                        {binasFiltradas.length === 0 ? (
                                            <div className="p-3 text-gray-400 text-sm text-center">Nenhum número encontrado</div>
                                        ) : (
                                            binasFiltradas.map(n => (
                                                <button
                                                    key={`phone-${n._id}`}
                                                    type="button"
                                                    onClick={() => {
                                                        if (n.sipAssociado) {
                                                            setFormData(prev => ({ ...prev, phoneNumberSelecionado: n._id }));
                                                            setIsBinaDropdownOpen(false);
                                                            setBinaFiltroNome('');
                                                        }
                                                    }}
                                                    disabled={!n.sipAssociado}
                                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors ${
                                                        formData.phoneNumberSelecionado === n._id ? 'bg-purple-600 text-white' : 'text-gray-200'
                                                    } ${!n.sipAssociado ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {n.name} ({n.number}) {!n.sipAssociado && '(Sem SIP válido)'}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <label htmlFor="agendamento" className={labelStyle}>Agendar Envio</label>
                        <div className="mb-3 p-3 bg-gray-700 border border-gray-600 rounded-md">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-300">Hora Atual do Servidor:</span>
                                <span className="text-base font-semibold text-purple-400">
                                    {horaAtual.format('DD/MM/YYYY HH:mm:ss')}
                                </span>
                            </div>
                        </div>
                        <input 
                            id="agendamento" 
                            name="agendamento" 
                            type="datetime-local" 
                            value={formData.agendamento} 
                            onChange={handleChange}
                            onFocus={() => setIsAgendamentoFocused(true)}
                            onBlur={() => {
                                setIsAgendamentoFocused(false);
                                // Atualizar minDateTime quando sair do campo
                                const novoMinuto = horaAtual.clone().startOf('minute').format('YYYY-MM-DDTHH:mm');
                                setMinDateTime(prevMin => prevMin !== novoMinuto ? novoMinuto : prevMin);
                            }}
                            min={minDateTime}
                            className={inputStyle} 
                            required 
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            A campanha será iniciada na data e hora especificadas (Fuso: São Paulo). 
                            <span className="text-red-400"> Não é possível agendar no passado.</span>
                        </p>
                    </div>
                    {/* Horário de Funcionamento (obrigatório) */}
                    <div className="border border-gray-600 rounded-lg p-4 bg-gray-700 bg-opacity-40">
                        <h3 className={`${labelStyle} mb-1`}>Horário de funcionamento <span className="text-red-400">*</span></h3>
                        <p className="text-xs text-gray-400 mb-4">
                            Fora do período configurado a campanha ficará pausada automaticamente (sem fazer ligações).
                        </p>
                        <div className="space-y-4 pl-6 border-l-2 border-purple-500">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelStyle}>Período - Data Início <span className="text-red-400">*</span></label>
                                        <input
                                            type="date"
                                            name="funcionamento.dataInicio"
                                            value={formData.funcionamento?.dataInicio || ''}
                                            onChange={handleChange}
                                            className={inputStyle}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Período - Data Fim <span className="text-red-400">*</span></label>
                                        <input
                                            type="date"
                                            name="funcionamento.dataFim"
                                            value={formData.funcionamento?.dataFim || ''}
                                            onChange={handleChange}
                                            className={inputStyle}
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelStyle}>Dias da semana para operar <span className="text-red-400">*</span></label>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {[
                                            { val: 0, lbl: 'Dom' },
                                            { val: 1, lbl: 'Seg' },
                                            { val: 2, lbl: 'Ter' },
                                            { val: 3, lbl: 'Qua' },
                                            { val: 4, lbl: 'Qui' },
                                            { val: 5, lbl: 'Sex' },
                                            { val: 6, lbl: 'Sáb' }
                                        ].map(({ val, lbl }) => (
                                            <button
                                                key={val}
                                                type="button"
                                                onClick={() => toggleDiaSemana(val)}
                                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                    (formData.funcionamento?.diasSemana || []).includes(val)
                                                        ? 'bg-purple-600 text-white'
                                                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                                }`}
                                            >
                                                {lbl}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelStyle}>Horário Início</label>
                                        <input
                                            type="time"
                                            name="funcionamento.horaInicio"
                                            value={formData.funcionamento?.horaInicio || '08:00'}
                                            onChange={handleChange}
                                            className={inputStyle}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelStyle}>Horário Fim</label>
                                        <input
                                            type="time"
                                            name="funcionamento.horaFim"
                                            value={formData.funcionamento?.horaFim || '18:00'}
                                            onChange={handleChange}
                                            className={inputStyle}
                                        />
                                    </div>
                                </div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="qtdTentativasPorNumero" className={labelStyle}>Número de Tentativas por Número</label>
                        <select id="qtdTentativasPorNumero" name="qtdTentativasPorNumero" value={formData.qtdTentativasPorNumero} onChange={handleChange} className={inputStyle} required>
                            {[1, 2, 3, 4, 5].map(num => (
                                <option key={`tentativas-${num}`} value={num}>
                                    {num} tentativas
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Número de tentativas de ligação para cada número antes de desistir (1 a 5).</p>
                    </div>
                    <div className="border border-gray-600 rounded-lg p-4 bg-gray-700 bg-opacity-40">
                        <div className="flex items-center gap-2 mb-3">
                            <input
                                type="checkbox"
                                id="followUpConfig.habilitado"
                                name="followUpConfig.habilitado"
                                checked={!!formData.followUpConfig?.habilitado}
                                onChange={handleChange}
                                className="rounded border-gray-500 text-purple-600"
                            />
                            <label htmlFor="followUpConfig.habilitado" className={`${labelStyle} cursor-pointer`}>Ativar Follow-up automático</label>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">
                            Quando ativo, contatos sem atendimento/erro retentável entram em nova tentativa conforme regras abaixo.
                        </p>
                        {formData.followUpConfig?.habilitado && (
                            <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className={labelStyle}>Máximo total</label>
                                    <input type="number" min="1" max="20" name="followUpConfig.maxTentativasTotal" value={formData.followUpConfig?.maxTentativasTotal ?? 4} onChange={handleChange} className={inputStyle} />
                                </div>
                                <div>
                                    <label className={labelStyle}>Máximo por dia</label>
                                    <input type="number" min="1" max="10" name="followUpConfig.maxTentativasPorDia" value={formData.followUpConfig?.maxTentativasPorDia ?? 1} onChange={handleChange} className={inputStyle} />
                                </div>
                                <div>
                                    <label className={labelStyle}>Intervalo (minutos)</label>
                                    <input type="number" min="1" max="10080" name="followUpConfig.intervaloMinutos" value={formData.followUpConfig?.intervaloMinutos ?? 1440} onChange={handleChange} className={inputStyle} />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className={labelStyle}>Início dos disparos</label>
                                    <input
                                        type="time"
                                        name="followUpConfig.horaInicioDisparos"
                                        value={formData.followUpConfig?.horaInicioDisparos || '08:00'}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label className={labelStyle}>Fim dos disparos</label>
                                    <input
                                        type="time"
                                        name="followUpConfig.horaFimDisparos"
                                        value={formData.followUpConfig?.horaFimDisparos || '18:00'}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        id="followUpConfig.reativarCicloAposLimiteAtivo"
                                        name="followUpConfig.reativarCicloAposLimiteAtivo"
                                        type="checkbox"
                                        checked={!!formData.followUpConfig?.reativarCicloAposLimiteAtivo}
                                        onChange={handleChange}
                                        className="rounded border-gray-500 text-purple-600"
                                    />
                                    <label htmlFor="followUpConfig.reativarCicloAposLimiteAtivo" className={labelStyle}>Reativar ciclo após atingir limite</label>
                                </div>
                                {formData.followUpConfig?.reativarCicloAposLimiteAtivo && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelStyle}>Reativar após</label>
                                            <input type="number" min="1" max="120" name="followUpConfig.reativarCicloQuantidade" value={formData.followUpConfig?.reativarCicloQuantidade ?? 0} onChange={handleChange} className={inputStyle} />
                                        </div>
                                        <div>
                                            <label className={labelStyle}>Unidade</label>
                                            <select name="followUpConfig.reativarCicloUnidade" value={formData.followUpConfig?.reativarCicloUnidade || 'semanas'} onChange={handleChange} className={inputStyle}>
                                                <option value="dias">Dias</option>
                                                <option value="semanas">Semanas</option>
                                                <option value="meses">Meses</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4">
                                <label className={labelStyle}>Números excluídos do Follow-up</label>
                                <textarea
                                    name="followUpConfig.numerosExcluidos"
                                    value={formData.followUpConfig?.numerosExcluidos || ''}
                                    onChange={handleChange}
                                    className={inputStyle}
                                    placeholder="Um número por linha. Esses contatos não entrarão no Follow-up."
                                    rows={4}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className={labelStyle}>Assistente no Follow-up</label>
                                    <select
                                        name="followUpConfig.assistenteFollowUpId"
                                        value={formData.followUpConfig?.assistenteFollowUpId || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    >
                                        <option value="">Mesmo assistente da campanha</option>
                                        {assistentes.map((a) => (
                                            <option key={a.vapiId || a._id} value={a.vapiId || a._id}>{a.nome || a.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelStyle}>Número (bina) no Follow-up</label>
                                    <select
                                        name="followUpConfig.phoneNumberFollowUpId"
                                        value={formData.followUpConfig?.phoneNumberFollowUpId || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                    >
                                        <option value="">Mesmo número da campanha</option>
                                        {phoneNumbers.map((pn) => (
                                            <option key={pn.vapiId || pn._id} value={pn.vapiId || pn._id}>{pn.name} ({pn.number})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label className={labelStyle}>Token da integração de mensagem</label>
                                    <input
                                        type="text"
                                        name="followUpConfig.uazapiToken"
                                        value={formData.followUpConfig?.uazapiToken || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                        placeholder="Token da integração de mensagem para notificação de limite"
                                    />
                                </div>
                                <div>
                                    <label className={labelStyle}>Mensagem ao atingir limite</label>
                                    <textarea
                                        name="followUpConfig.uazapiMensagemLimite"
                                        value={formData.followUpConfig?.uazapiMensagemLimite || ''}
                                        onChange={handleChange}
                                        className={inputStyle}
                                        placeholder="Mensagem enviada quando atingir o limite de follow-up (aceita quebra de linha)"
                                        rows={4}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                <label className={labelStyle}>Mensagens para cliente não atendido</label>
                                {(() => {
                                    const mensagens = Array.isArray(formData.followUpConfig?.uazapiMensagensNaoAtendeu)
                                        ? formData.followUpConfig.uazapiMensagensNaoAtendeu
                                        : [''];
                                    const mensagensVisiveis = showAllNaoAtendeuMessages ? mensagens : mensagens.slice(0, 2);
                                    return mensagensVisiveis.map((msg, idx) => (
                                    <div key={`nao-atendeu-campanha-${idx}`} className="rounded-md border border-gray-600 p-2 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-400">Mensagem {idx + 1}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeNaoAtendeuMessage(idx)}
                                                className="px-2 py-1 text-xs bg-gray-600 rounded hover:bg-gray-500 disabled:opacity-50"
                                                disabled={mensagens.length <= 1}
                                            >
                                                Remover
                                            </button>
                                        </div>
                                        <textarea
                                            value={msg}
                                            onChange={(e) => handleNaoAtendeuMessageChange(idx, e.target.value)}
                                            className={inputStyle}
                                            placeholder="Escreva a mensagem (quebras de linha são permitidas)"
                                            rows={4}
                                        />
                                    </div>
                                    ));
                                })()}
                                {(Array.isArray(formData.followUpConfig?.uazapiMensagensNaoAtendeu)
                                    ? formData.followUpConfig.uazapiMensagensNaoAtendeu.length
                                    : 0) > 2 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllNaoAtendeuMessages((prev) => !prev)}
                                        className="px-3 py-1 text-sm text-gray-300 hover:text-white underline underline-offset-2"
                                    >
                                        {showAllNaoAtendeuMessages ? 'Ver menos' : 'Ver mais'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={addNaoAtendeuMessage}
                                    className="px-3 py-2 text-sm bg-transparent border border-gray-500 rounded hover:bg-gray-700"
                                >
                                    + Adicionar mensagem
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Deixe em branco para repetir assistente e bina da campanha nas tentativas de follow-up.</p>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isSubmitting || loadingInitialData} className="flex items-center justify-center w-full md:w-auto px-6 py-3 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50">
                            <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                            {isSubmitting ? 'Agendando...' : 'Agendar Campanha'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
